/**
 * Logo Cache Service
 * Pre-loads and caches the PNG logo from local file (no CORS issues)
 * Falls back to Firebase Storage if local file is not available
 */

const LOCAL_LOGO_URL = '/saheeh_logo_w.svg';
const FIREBASE_LOGO_URL = '/saheeh_logo_w.svg';

class LogoCacheService {
  private static cachedLogo: string | null = null;
  private static loadingPromise: Promise<string> | null = null;

  /**
   * Get cached logo or load it if not cached
   * Prioritizes local file (no CORS) over Firebase Storage
   */
  static async getLogo(): Promise<string | null> {
    // Return cached logo if available
    if (this.cachedLogo) {
      console.log('✅ [LOGO CACHE] Using cached logo');
      return this.cachedLogo;
    }

    // If already loading, return the existing promise
    if (this.loadingPromise) {
      console.log('⏳ [LOGO CACHE] Logo already loading, waiting...');
      return this.loadingPromise;
    }

    // Start loading (prioritizes local file)
    this.loadingPromise = this.loadLogo();
    try {
      this.cachedLogo = await this.loadingPromise;
      return this.cachedLogo;
    } catch (error) {
      console.error('❌ [LOGO CACHE] Failed to load logo from all sources:', error);
      this.loadingPromise = null;
      return null;
    }
  }

  /**
   * Load logo - tries local file first (no CORS), then Firebase Storage
   */
  private static async loadLogo(): Promise<string> {
    // Method 1: Try loading from local file first (same origin, no CORS)
    try {
      console.log('🖼️ [LOGO CACHE] Attempting to load logo from local file:', LOCAL_LOGO_URL);
      const localResponse = await fetch(LOCAL_LOGO_URL, { cache: 'no-cache' });
      
      if (localResponse.ok) {
        const blob = await localResponse.blob();
        console.log('📦 [LOGO CACHE] Logo blob received:', { size: blob.size, type: blob.type });
        
        if (blob.size === 0) {
          throw new Error('Logo blob is empty');
        }
        
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (reader.result && typeof reader.result === 'string' && reader.result.startsWith('data:image/')) {
              console.log('✅ [LOGO CACHE] Logo loaded from local file (no CORS issues), length:', reader.result.length);
              resolve(reader.result);
            } else {
              reject(new Error('Invalid logo data from local file'));
            }
          };
          reader.onerror = () => reject(new Error('FileReader error'));
          reader.readAsDataURL(blob);
        });
        
        return base64;
      } else {
        throw new Error(`Local logo not found: ${localResponse.status} ${localResponse.statusText}`);
      }
    } catch (localError) {
      console.warn('⚠️ [LOGO CACHE] Local logo not available, trying Firebase Storage:', localError);
      
      // Method 2: Fallback to Firebase Storage using Image element (may have CORS issues)
      return new Promise((resolve, reject) => {
        console.log('🖼️ [LOGO CACHE] Loading logo from Firebase Storage...');

        const img = new Image();
        img.crossOrigin = 'anonymous';

        // Set timeout
        const timeout = setTimeout(() => {
          reject(new Error('Logo load timeout after 15 seconds'));
        }, 15000);

        img.onload = () => {
          clearTimeout(timeout);
          try {
            console.log('✅ [LOGO CACHE] Image loaded, converting to base64...');
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth || img.width || 800;
            canvas.height = img.naturalHeight || img.height || 300;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              reject(new Error('Failed to get canvas context'));
              return;
            }

            ctx.drawImage(img, 0, 0);
            const pngDataUrl = canvas.toDataURL('image/png');

            console.log('✅ [LOGO CACHE] Logo converted to base64, length:', pngDataUrl.length);
            resolve(pngDataUrl);
          } catch (error) {
            console.error('❌ [LOGO CACHE] Canvas conversion error:', error);
            reject(error);
          }
        };

        img.onerror = (error) => {
          clearTimeout(timeout);
          console.error('❌ [LOGO CACHE] Image load error (CORS may be blocking):', error);
          reject(new Error('Failed to load logo image - CORS may be blocking'));
        };

        img.src = FIREBASE_LOGO_URL;
      });
    }
  }

  /**
   * Clear cache (useful for testing or if logo needs to be reloaded)
   */
  static clearCache(): void {
    this.cachedLogo = null;
    this.loadingPromise = null;
    console.log('🗑️ [LOGO CACHE] Cache cleared');
  }
}

export default LogoCacheService;

